import React, { useCallback, useState } from 'react';
import isEmpty from 'lodash/isEmpty';

import { CategoryID } from '../../../../categories/categoriesTypes';
import { IconsEnum } from '../../../../../assets/icons/types';
import { ItemsFilters } from '../../../../../types';

import { useThreeDStockLifestylesCategories } from '../../../hooks/useThreeDStockLifestylesCategories';

import {
  ThreeDStockMenuCategoriesNav,
  ThreeDStockMenuCategoriesNavCategory
} from '../ThreeDStockMenuCategoriesNav';

import { PureIconButtonHelper } from '../../../../../helpers/buttons/PureIconButtonHelper';
import { AlertMessage } from '../../../../../helpers/AlertMessage';
import { NextPureLinkHelper } from '../../../../../helpers/links/NextPureLinkHelper';
import { LoadingSkeleton } from '../../../../../helpers/LoadingSkeleton';

import { getChangeLifestylesFiltersCategoriesData } from '../../../../lifestyles/utils/getChangeLifestylesFiltersCategoriesData';

import { lifestylesKeys } from '../../../../../locales/keys';
import { StockTabs } from '../../../stockConstants';

interface ThreeDStockMenuLifestylesCategoriesNavProps {
  categoryTitleHref: string;
  onClose: () => void;
  onSelectCategory: (id?: CategoryID) => void;
  changeItemsCategoryFilters?: (
    tab?: StockTabs,
    changeFilters?: ItemsFilters,
    removeFilters?: string[]
  ) => void;
  alwaysShowAllCategories?: boolean;
  disabledTitleLink?: boolean;
  clientIds?: string[];
}

function ThreeDStockMenuLifestylesCategoriesNav({
  categoryTitleHref,
  onClose,
  onSelectCategory,
  changeItemsCategoryFilters,
  alwaysShowAllCategories,
  disabledTitleLink,
  clientIds
}: ThreeDStockMenuLifestylesCategoriesNavProps) {
  const [currentCategory, setCurrentCategory] =
    useState<ThreeDStockMenuCategoriesNavCategory>(undefined);

  const lifestylesFilters = isEmpty(clientIds)
    ? {
        nda: false,
        blocked: false
      }
    : {
        blocked: false,
        clientIds
      };

  const {
    lifestyleCategoriesWithParents,
    lifestyleCategoriesErrorMessage,
    lifestyleCategoriesFetched,
    lifestyleCategoriesIsPlaceholderData
  } = useThreeDStockLifestylesCategories({
    lifestylesFilters
  });

  const handleChangeFilters = useCallback<
    (category: ThreeDStockMenuCategoriesNavCategory) => void
  >(
    (category) => {
      const { changeFilters, resetFilters } =
        getChangeLifestylesFiltersCategoriesData({
          lifestyleCategory: category,
          categories: lifestyleCategoriesWithParents
        });

      changeItemsCategoryFilters?.(
        StockTabs.LIFESTYLES,
        changeFilters,
        resetFilters
      );
    },
    [changeItemsCategoryFilters, lifestyleCategoriesWithParents]
  );

  const handleSetCurrentCategory = useCallback<
    (parentCategory?: ThreeDStockMenuCategoriesNavCategory) => void
  >(
    (parentCategory) => {
      setCurrentCategory(parentCategory);
      onSelectCategory(parentCategory?.id);
    },
    [onSelectCategory]
  );

  const handleSelectCategory = useCallback<
    (id: CategoryID, hasChildrenCategories?: boolean) => void
  >(
    (id, hasChildrenCategories) => {
      const selectedCategory = lifestyleCategoriesWithParents?.find(
        (category) => category.id === id
      );

      if (!hasChildrenCategories) {
        handleChangeFilters(selectedCategory);
        onClose();
        return;
      }

      handleSetCurrentCategory(selectedCategory);
    },
    [
      handleChangeFilters,
      handleSetCurrentCategory,
      lifestyleCategoriesWithParents,
      onClose
    ]
  );

  const handleBack = useCallback<() => void>(() => {
    const parentCategory = currentCategory?.parent?.id
      ? lifestyleCategoriesWithParents?.find(
          (category) => category.id === currentCategory?.parent?.id
        )
      : undefined;
    handleSetCurrentCategory(parentCategory);
  }, [
    currentCategory?.parent?.id,
    handleSetCurrentCategory,
    lifestyleCategoriesWithParents
  ]);

  return (
    <div className="py-8">
      {currentCategory ? (
        <div className="text-xl font-semibold px-4 mb-6 h-10 flex gap-2 items-center">
          <PureIconButtonHelper
            className="py-2 pl-2 pr-2 rounded-full inline-flex items-center leading-6 focus:ring-base hover:text-gray-950 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 focus:ring-offset-0 -ml-2 transition-colors duration-150 ease-out"
            icon={IconsEnum.ARROW_LEFT_OUTLINE}
            iconClassName="h-6 w-6"
            onClick={handleBack}
          />

          <div>{currentCategory.localizedName}</div>
        </div>
      ) : (
        <NextPureLinkHelper
          className="text-xl font-semibold mb-5 flex gap-2 items-center px-4 py-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-150 ease-out"
          i18nText={lifestylesKeys.singular}
          href={categoryTitleHref}
          disabled={disabledTitleLink}
        />
      )}

      <AlertMessage message={lifestyleCategoriesErrorMessage} />
      <LoadingSkeleton
        loaded={
          !lifestyleCategoriesIsPlaceholderData && lifestyleCategoriesFetched
        }
      >
        <ThreeDStockMenuCategoriesNav
          categories={lifestyleCategoriesWithParents}
          currentCategory={currentCategory}
          // disabled={disabled}
          selectCategory={handleSelectCategory}
          alwaysShowAll={alwaysShowAllCategories}
        />
      </LoadingSkeleton>
    </div>
  );
}

export default ThreeDStockMenuLifestylesCategoriesNav;
